// 📁 src/dashboard/DashboardLayout.jsx
import React, { useContext, useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { AuthContext } from '../context/AuthContext';
import LanguageSelector from '../components/LanguageSelector';
import {  LayoutDashboard, Building, Users, Settings, LogOut,
  Bell, CreditCard, UserCircle, GitMerge, MessageSquare, HelpCircle, User,
  Menu, X } from 'lucide-react';

export default function DashboardLayout() {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = () => setSidebarOpen(false);
  const toggleSidebar = (e) => {
    e.stopPropagation();
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (sidebarOpen && !event.target.closest('.sidebar')) {
        closeSidebar();
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [sidebarOpen]);

  const handleLogout = async () => {
    await logout(); // Assumes logout is provided by AuthContext
    navigate('/login');
  };

  const mainNavItems = [
    { id: 'menu.dashboard', path: '/dashboard', icon: LayoutDashboard, exact: true },
    { id: 'menu.properties', path: '/dashboard/properties', icon: Building },
    { id: 'menu.leads', path: '/dashboard/leads', icon: Users },
    { id: 'menu.notifications', path: '/dashboard/notifications', icon: Bell },
  ];

  const settingsNavItems = [
    { id: 'menu.users', path: '/dashboard/users', icon: Users },
    { id: 'menu.profile', path: '/dashboard/profile', icon: User },
    { id: 'menu.company-settings', path: '/dashboard/company-settings', icon: Settings },
    { id: 'menu.integration', path: '/dashboard/integration', icon: GitMerge },
    { id: 'menu.chatbot-settings', path: '/dashboard/chatbot-settings', icon: MessageSquare },
    { id: 'menu.email-lead-settings', path: '/dashboard/email-lead-settings', icon: MessageSquare },
    { id: 'menu.agent-assignment', path: '/dashboard/agent-assignment', icon: Users },
    { id: 'menu.subscription', path: '/dashboard/subscription', icon: CreditCard },
    { id: 'menu.help', path: '/dashboard/help', icon: HelpCircle },
  ];

  const renderNavLinks = (items) => items.map(item => (
    <li key={item.path}>
      <NavLink to={item.path} end={item.exact} className={({ isActive }) => isActive ? 'active' : ''} onClick={closeSidebar}>
        <item.icon size={18} />
        <span>{intl.formatMessage({ id: item.id })}</span>
      </NavLink>
    </li>
  ));

  return (
    <div className="dashboard-layout">
      <button className="menu-toggle" onClick={toggleSidebar}>
        <Menu size={20} />
      </button>

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="logo-container">
          <img src="/lugarlogopw.png" alt="LugarAI" className="logo" />
          <button className="menu-toggle" onClick={closeSidebar}>
            <X size={20} color="#fff" />
          </button>
        </div>
        <nav className="sidebar-nav">
          <ul>
            {renderNavLinks(mainNavItems)}
            {renderNavLinks(settingsNavItems)}
            <li>
              <a href="#" onClick={(e) => { e.preventDefault(); handleLogout(); }} className="nav-button">
                <LogOut size={18} />
                <span>{intl.formatMessage({ id: 'menu.logout' })}</span>
              </a>
            </li>
          </ul>
        </nav>
        <div className="sidebar-language-selector">
          <LanguageSelector />
        </div>
      </aside>

      <main className="dashboard-content">
        <div className="dashboard-topbar">
          <div className="topbar-left">
            {/* Opcional: Barra de busca global pode ser adicionada aqui */}
          </div>
          <div className="topbar-user">
            <img src={user?.photoURL || '/default-avatar.png'} alt="User" className="avatar" />
            <div className="user-info">
              <span className="user-name">
                {user?.name || user?.displayName || intl.formatMessage({ id: 'user.Label' })}
              </span>
              {user?.creci && (
                <span className="user-creci">CRECI: {user.creci}</span>
              )}
            </div>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
